import * as React from "react";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";

import CardContent from "@mui/material/CardContent";

import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import { ASSETS } from "../../utils/config";
import { Button, Stack, TextField } from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckIcon from "@mui/icons-material/Check";
import { useState, useEffect, useRef } from "react";
import SnackBar from "../SnackBar";
import { localStorageHelper } from "../../utils/localStorageHelper";
import CircularProgress from "@mui/material/CircularProgress";
import { generateAICompletion, generateAITitles } from "../../services/api";
import { toast } from "react-toastify";
import { AI_ASSISTANT_MEMORY_ENABLED } from "./flag";
import {
  getConversation,
  appendMessage,
  setDraft,
  composeContextPrompt,
  lastAssistantMessage,
  subscribe,
} from "./conversationStore";

// Thin React binding over the module-level conversation store. Re-renders when
// the given block's thread/draft changes. When `key` is null (flag OFF or no
// block id) it is inert and just returns the shared EMPTY conversation.
function useConversation(key) {
  const [conv, setConv] = useState(() => getConversation(key));
  useEffect(() => {
    setConv(getConversation(key));
    const unsub = subscribe(key, setConv);
    return unsub;
  }, [key]);
  return conv;
}

export default function NewAIPrompt({
  onCloseIconPress = () => {},
  modalWidth = "424px",
  completionType = "description",
  // Stable per-block id (craft.js node id). When the memory flag is ON and a key
  // is provided, the conversation is kept in the module-level store keyed by this
  // id, so it survives the popup closing/reopening and re-selecting the block.
  conversationKey = null,
}) {
  const memoryOn = AI_ASSISTANT_MEMORY_ENABLED && !!conversationKey;

  // Conversation thread (memory mode only). Inert when memoryOn is false.
  const conv = useConversation(memoryOn ? conversationKey : null);

  // Local-only state used by the legacy (flag OFF) single-shot path.
  const [localTextValue, setLocalTextValue] = useState("");
  const [title, setTitle] = useState("");
  const [aiGeneratedResponse, setaiGeneratedResponse] = useState("");

  const [copied, setcopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const contentRef = React.useRef(null);
  const modalRef = useRef(null);

  const [tokensLeft, settokensLeft] = useState(
    localStorageHelper.getAItokenLeft()
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Effective input value: the store's draft in memory mode, else local state.
  const textvalue = memoryOn ? conv.draft : localTextValue;
  const updateText = (v) => {
    if (memoryOn) setDraft(conversationKey, v);
    else setLocalTextValue(v);
  };

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onCloseIconPress();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [onCloseIconPress]);

  // Reset copied state after timeout
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setcopied(false);
      }, 2000); // Reset after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [copied]);

  // In memory mode, keep the thread scrolled to the newest turn.
  useEffect(() => {
    if (memoryOn && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [memoryOn, conv.messages.length, loading]);

  const toggleTooltip = () => {
    setTooltipOpen(!tooltipOpen);
  };

  //saved for later
  const isDisabled = textvalue.trim() === "" || loading;

  // Memory-mode submit: append the turn to the per-block store and send the
  // recent history along so the (stateless) endpoint can continue the thread.
  const handleSubmitMemory = async () => {
    const promptText = textvalue.trim();
    if (!promptText || loading) return;

    setLoading(true);
    setcopied(false);
    setDraft(conversationKey, ""); // optimistically clear the input

    try {
      const composedPrompt = composeContextPrompt(conversationKey, promptText);
      let assistantText;

      if (completionType === "title") {
        const response = await generateAITitles(composedPrompt);
        assistantText =
          (response.data.suggestions &&
            response.data.suggestions.length > 0 &&
            response.data.suggestions[0].text) ||
          "";
      } else {
        const response = await generateAICompletion({
          prompt: composedPrompt,
          completion_type: completionType,
          max_tokens: 1000,
        });
        const completion = response.data.completion || response.data;
        assistantText =
          typeof completion === "string"
            ? completion
            : String(completion == null ? "" : completion);
      }

      // Commit both turns only once the reply is in hand, so a failed request
      // never leaves a dangling user turn with no answer.
      appendMessage(conversationKey, "user", promptText);
      appendMessage(conversationKey, "assistant", assistantText);
    } catch (error) {
      console.error("Error making request:", error);
      setDraft(conversationKey, promptText); // restore so the user can retry
      toast.error("Failed to generate AI content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (memoryOn) {
      return handleSubmitMemory();
    }

    if (!textvalue.trimEnd()) {
      return console.error("Empty Input");
    }

    if (isDisabled) {
      return;
    }

    setLoading(true);
    setTitle(textvalue);
    setcopied(false);

    try {
      let response;

      // Determine if we need multiple suggestions (only for title completion_type)
      const needsMultipleSuggestions = completionType === "title";

      if (needsMultipleSuggestions) {
        // Use titles API for title suggestions (GET request)
        response = await generateAITitles(textvalue);
      } else {
        // Use completion API for single completion
        response = await generateAICompletion({
          prompt: textvalue,
          completion_type: completionType,
          max_tokens: 1000,
        });
      }

      setLoading(false);
      updateText("");

      // Show the response container
      const parentTextView = document.getElementById("ParentTextView");
      if (parentTextView) {
        parentTextView.style.display = "block";
      }

      // Handle response based on whether it's single or multiple suggestions
      if (needsMultipleSuggestions) {
        // Handle titles API response (returns suggestions array like the old suggestions API)
        if (response.data.suggestions && response.data.suggestions.length > 0) {
          // Use only the first suggestion entry (which contains multiple titles)
          const firstSuggestionText = response.data.suggestions[0].text;

          const typodiv = document.querySelector("#aiGeneratedResponse");
          typodiv.innerText = firstSuggestionText;

          // Store the first suggestion's text as the main response for copying
          setaiGeneratedResponse(firstSuggestionText);
        }
      } else {
        // Single completion
        const completion = response.data.completion || response.data;
        const typodiv = document.querySelector("#aiGeneratedResponse");
        typodiv.innerText = completion;
        setaiGeneratedResponse(completion);

        // Scroll to bottom
        if (contentRef.current) {
          const element = contentRef.current;
          element.scrollTop = element.scrollHeight;
        }
      }

      console.log("AI Response:", response.data);
    } catch (error) {
      console.error("Error making request:", error);
      setLoading(false);
      toast.error("Failed to generate AI content. Please try again.");
    }
  };

  const handleChange = (e) => {
    updateText(e.target.value);
  };

  const [open, setOpen] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const copyText = memoryOn
    ? lastAssistantMessage(conversationKey)
    : aiGeneratedResponse;

  const handleCopy = (text) => {
    const value = typeof text === "string" ? text : copyText;
    navigator.clipboard.writeText(value);
    setcopied(true);
    setOpen(true);
  };

  return (
    <div ref={modalRef}>
      <Card
        sx={{
          width: modalWidth ? modalWidth : "424px",
          borderRadius: "8px",
          marginX: 0,
          border: "1px solid #123051",
          paddingBottom: "15px",
          maxHeight: "35vh",
          overflowY: "auto",
        }}
      >
        <SnackBar
          open={open}
          handleClose={handleClose}
          message="Text Copied Successfully"
        ></SnackBar>

        <div
          style={{
            padding: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#F6F7F9",
            borderBottom: "1px solid #123051",
          }}
        >
          <Stack direction={"row"}>
            <Typography variant="subtitle" color="text.secondary">
              AI Assistant
            </Typography>
          </Stack>
          <Stack direction={"row-reverse"}>
            <CloseSharpIcon
              style={{ cursor: "pointer" }}
              onClick={onCloseIconPress}
            />
          </Stack>
        </div>

        {/* Memory mode: render the running conversation thread for this block. */}
        {memoryOn && conv.messages.length > 0 && (
          <div
            ref={contentRef}
            style={{
              maxHeight: "22vh",
              overflowY: "auto",
              padding: "12px 15px 0",
            }}
          >
            {conv.messages.map((m, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent:
                    m.role === "user" ? "flex-end" : "flex-start",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    maxWidth: "85%",
                    padding: "8px 10px",
                    borderRadius: 8,
                    fontSize: 12,
                    lineHeight: 1.35,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    backgroundColor:
                      m.role === "user"
                        ? "rgba(18, 48, 81, 0.08)"
                        : "rgba(33, 150, 243, 0.06)",
                    color: "rgba(86, 85, 86, 1)",
                  }}
                >
                  {m.content}
                  {m.role === "assistant" && (
                    <div
                      onClick={() => handleCopy(m.content)}
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        color: copied ? "#4CAF50" : "rgba(18, 48, 81, 1)",
                        cursor: "pointer",
                      }}
                    >
                      {copied ? "Copied" : "Copy"}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Stack p={2} mb={-2}>
          <Typography
            sx={{ fontSize: "14px", color: "#565556", fontWeight: 400 }}
          >
            Enter your prompt here:
          </Typography>
          <div
            style={{
              // maxHeight: "20vh",
              marginTop: "10px",
              marginBottom: "10px",

              borderRadius: 5,
              border: "1px solid rgba(225, 225, 225, 1)",
            }}
          >
            <TextField
              value={textvalue}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isDisabled) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              id="outlined-multiline-static"
              multiline
              minRows={3}
              maxRows={9}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
                "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "transparent",
                  },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "transparent",
                  },
                "& .MuiOutlinedInput-input": {
                  padding: 0,
                  fontWeight: "400",
                  color: "#565556",
                  fontSize: "12px",
                  lineHeight: 1.15,
                },
              }}
              placeholder="e.g Generate a list of movies according to imdb for people to watch in their 20s for success"
            />
            {/* <Divider style={{ margin: 0, padding: 0 }}></Divider> */}
          </div>
        </Stack>
        <div
          style={{
            justifyContent: "flex-end",
            alignItems: "center",
            display: "flex",
          }}
        >
          {loading ? (
            <Stack
              justifyContent={"center"}
              alignItems={"center"}
              direction={"row"}
              m={1}
              mr={3}
            >
              <CircularProgress sx={{ mr: 1 }} disableShrink size={12} />
              <Typography sx={{ fontSize: 12 }}>Generating...</Typography>
            </Stack>
          ) : (
            <Stack
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              alignSelf={"flex-end"}
              alignItems={"center"}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#565556",
                  fontWeight: 400,
                  mr: 1.5,
                  cursor: "pointer",
                }}
                onClick={() => updateText("")}
              >
                Clear
              </Typography>
              <div
                onClick={handleSubmit}
                style={{
                  backgroundColor: !isDisabled
                    ? "rgba(18, 48, 81, 1)"
                    : "rgb(235,235,228)",
                  border: !isDisabled ? "1px solid black" : "1px solid white",
                  borderRadius: "4px",
                  width: "80px",
                  fontSize: "14px",
                  fontFamily: "DM Sans",
                  fontWeight: "400",
                  color: !isDisabled ? "rgba(255, 255, 255, 1)" : "#5A5A5A",
                  cursor: !isDisabled ? "pointer" : "auto",
                  marginRight: "15px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {" "}
                {memoryOn && conv.messages.length > 0 ? "Send" : "Generate"}
              </div>
            </Stack>
          )}
        </div>

        {/* Legacy (flag OFF) single-shot response container. Left untouched so
            the OFF path renders byte-identically to before. */}
        {!memoryOn && (
          <div
            id="ParentTextView"
            style={{
              display: "none",
              borderRadius: "5px",
              backgroundColor: "rgba(33, 150, 243, 0.04)",
              margin: "15px",
              marginBottom: "1px",
            }}
          >
            <CardContent
              ref={contentRef}
              style={{
                maxHeight: "35vh",
                overflowY: "auto",
              }}
            >
              <Typography
                id="aiGeneratedResponse"
                fontSize={12}
                color={"rgba(86, 85, 86, 1)"}
                paragraph
              ></Typography>
            </CardContent>
            <CardContent
              style={{
                borderBottomRightRadius: 20,
                borderBottomLeftRadius: 20,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {aiGeneratedResponse && (
                <Stack
                  onClick={handleCopy}
                  sx={{
                    backgroundColor: "#E1E1E1",
                    paddingX: 1,
                    paddingY: 0.5,
                    borderRadius: 5,
                    cursor: "pointer",
                  }}
                  direction={"row-reverse"}
                  alignItems={"center"}
                >
                  <Typography
                    fontWeight={700}
                    sx={{ marginLeft: 0.5 }}
                    fontSize={12}
                    color={copied ? "#4CAF50" : "rgba(18, 48, 81, 1)"}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Typography>
                  {copied ? (
                    <CheckIcon
                      sx={{
                        color: "#4CAF50",
                        width: 14,
                        height: 14,
                        marginRight: 0.5,
                      }}
                    />
                  ) : (
                    <img
                      width={10}
                      height={10}
                      src={ASSETS.copyIcon}
                      alt="Copy"
                    />
                  )}
                </Stack>
              )}
            </CardContent>
          </div>
        )}
      </Card>
    </div>
  );
}
